import {
  CompoundEntityRef,
  Entity,
  parseEntityRef,
  stringifyEntityRef,
} from '@backstage/catalog-model';

export function stringifyEntityRefSafe(
  ref:
    | Entity
    | {
        kind: string;
        namespace?: string;
        name: string;
      },
): string | undefined {
  try {
    const stringified = stringifyEntityRef(ref);
    return stringified;
  } catch (e) {
    return undefined;
  }
}

/**
 * Returns whether two refs are equal, refs can be either in compact form (es: user:test) or expanded form (es: user:default/test)
 * @param ref1
 * @param ref2
 * @returns whether the two refs are equal
 * @throws Error if either ref is not a valid entity ref
 */
export function areRefsEqual(ref1: string, ref2: string): boolean {
  const expanded1 = expandEntityRef(ref1);
  const expanded2 = expandEntityRef(ref2);
  return expanded1 === expanded2;
}

export function expandEntityRefSafe(ref: string): string | null {
  try {
    const expandedRef = expandEntityRef(ref);
    return expandedRef;
  } catch (e) {
    return null;
  }
}

/**
 * Generates a reference to an entity in the catalog that always has kind, namespace, name
 * @param ref - input reference to an entity
 * @returns reference to an entity with kind, namespace, name
 */
export function expandEntityRef(ref: string): string {
  return stringifyEntityRef(parseEntityRef(ref));
}

/**
 * Takes an entityRef and transforms it a compact entity ref like "user:name.surname.." used in the marketplace.
 *
 * @param entityRef
 */
export function compactStringRef(entityRef: string) {
  const compoundEntityRef = parseEntityRef(entityRef);
  return compactCompoundEntityRef(compoundEntityRef);
}

/**
 * Takes an entityRef and transforms it a compact entity ref like "user:name.surname.." used in the marketplace.
 *
 * @param compoundEntityRef
 */
export function compactCompoundEntityRef(compoundEntityRef: CompoundEntityRef) {
  return `${compoundEntityRef.kind.toLowerCase()}:${compoundEntityRef.name.toLowerCase()}`;
}

/**
 * Entity kinds that are considered Project-related entities.
 * These entities are directly associated with specific projects/systems.
 */
const PROJECT_ENTITY_KINDS = ['system', 'component', 'release', 'location'];

/**
 * Checks if the given entity is a Project-related entity.
 *
 * @param entity - The entity to check
 * @returns True if the entity is a Project-related entity
 */
export function isProjectEntity(entity: Entity): boolean {
  return PROJECT_ENTITY_KINDS.includes(entity.kind.toLowerCase());
}

/**
 * Checks if the given entity is a Platform Entity (i.e., not a Project-related entity).
 *
 * @param entity - The entity to check
 * @returns True if the entity is a Platform Entity, false otherwise
 */
export function isPlatformEntity(entity: Entity): boolean {
  return !isProjectEntity(entity);
}
